import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  CircularProgress,
  Link,
} from '@mui/material';
import React, { useCallback, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { DATE_FORMATS } from '@/consts/date.consts';
import { color } from '@/colors';
import modelIconMap from '@/components/Recipe/FlowComponents/LeftPanelSections/NodeIconMap';
import { I18N_KEYS } from '@/language/keys';
import { useSettingsStore } from '@/state/settings.state';
import useWorkspacesStore from '@/state/workspaces.state';
import { ModelDefinition } from '@/types/api/modelDefinition';
import { AppSwitch } from '@/UI/AppSwitch/AppSwitch';
import { Input } from '@/UI/Input/Input';
import { SearchIcon } from '@/UI/Icons/SearchIcon';
import { Tag } from '@/UI/Tag/Tag';
import { FlexCol, FlexCenVer, Flex } from '@/UI/styles';
import { VerifiedSeal } from '@/components/Common/NodeDataCard/VerifiedSeal';
import { XIcon } from '@/UI/Icons';
import { LabelSmRg, TextSmMd, TextSmRg } from '@/UI/AppTypography/AppTypography';
import { StyledTableCell, StyledTableRow } from '../../Team/TeamTable/TeamTable.styles';
import { LoadingSkeleton } from '../../LoadingSkeleton';
import { ModelFilters, BooleanFilter } from './ModelFilters';

type Order = 'asc' | 'desc';

type HeadCellId = keyof Pick<ModelDefinition, 'name' | 'createdAt' | 'isAllowed' | 'isVerified'> | 'terms';

interface HeadCell {
  id: HeadCellId;
  label: string;
  sortable?: boolean;
}

interface ModelTableProps {
  models: ModelDefinition[];
  isLoading: boolean;
}

interface FiltersState {
  search: string;
  allowedFilter: BooleanFilter;
  statusFilter: BooleanFilter;
}

type FiltersAction =
  | { type: 'SET_SEARCH'; payload: string }
  | { type: 'SET_ALLOWED_FILTER'; payload: BooleanFilter }
  | { type: 'SET_STATUS_FILTER'; payload: BooleanFilter }
  | { type: 'RESET_FILTERS' };

const initialFiltersState: FiltersState = {
  search: '',
  allowedFilter: 'all',
  statusFilter: 'all',
};

const filtersReducer = (state: FiltersState, action: FiltersAction): FiltersState => {
  switch (action.type) {
    case 'SET_SEARCH':
      return { ...state, search: action.payload };
    case 'SET_ALLOWED_FILTER':
      return { ...state, allowedFilter: action.payload };
    case 'SET_STATUS_FILTER':
      return { ...state, statusFilter: action.payload };
    case 'RESET_FILTERS':
      return initialFiltersState;
    default:
      return state;
  }
};

const getColWidth = (id: HeadCellId) => {
  switch (id) {
    case 'isAllowed':
      return '60px';
    default:
      return 'auto';
  }
};

export const ModelTable = ({ models, isLoading }: ModelTableProps) => {
  const [filtersState, dispatch] = useReducer(filtersReducer, initialFiltersState);
  const [orderBy, setOrderBy] = useState<HeadCellId>('name');
  const [order, setOrder] = useState<Order>('asc');
  const { t } = useTranslation();
  const activeWorkspace = useWorkspacesStore((state) => state.activeWorkspace);
  const { workspaceId } = activeWorkspace;
  const shouldBlockUnverifiedModels = Boolean(activeWorkspace.preferences?.shouldBlockUnverifiedModels);
  const updateModelAllowedStatus = useSettingsStore((state) => state.updateModelAllowedStatus);
  const updatingModelIds = useSettingsStore((state) => state.updatingModelIds);

  const headCells: HeadCell[] = useMemo(() => {
    const cells: HeadCell[] = [
      { id: 'name', label: t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.TABLE.HEADER_NAME), sortable: true },
      { id: 'isVerified', label: t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.TABLE.STATUS), sortable: true },
      { id: 'createdAt', label: t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.TABLE.HEADER_DATE_ADDED), sortable: true },
      { id: 'isAllowed', label: t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.TABLE.HEADER_APPROVED), sortable: true },
    ];

    if (!shouldBlockUnverifiedModels) {
      cells.splice(2, 0, { id: 'terms', label: t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.TABLE.TERMS) });
    }

    return cells;
  }, [t, shouldBlockUnverifiedModels]);

  const handleAllowedToggle = useCallback(
    (modelId: string, allowed: boolean) => {
      void updateModelAllowedStatus(workspaceId, modelId, allowed);
    },
    [workspaceId, updateModelAllowedStatus],
  );

  const handleRequestSort = useCallback(
    (property: HeadCellId) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [orderBy, order],
  );

  const filteredAndSortedModels = useMemo(() => {
    if (!models?.length) {
      return [];
    }

    let filtered = [...models];

    if (filtersState.search) {
      filtered = filtered.filter(
        (model) =>
          model?.name?.toLowerCase().includes(filtersState.search.toLowerCase()) ||
          model?.displayName?.toLowerCase().includes(filtersState.search.toLowerCase()) ||
          model?.description?.toLowerCase().includes(filtersState.search.toLowerCase()),
      );
    }

    if (filtersState.allowedFilter !== 'all') {
      filtered = filtered.filter((model) => (filtersState.allowedFilter ? model.isAllowed : !model.isAllowed));
    }

    if (filtersState.statusFilter !== 'all') {
      filtered = filtered.filter((model) => (filtersState.statusFilter ? model.isVerified : !model.isVerified));
    }

    return filtered.sort((a, b) => {
      // Handle special cases for non-direct properties
      if (orderBy === 'terms') {
        return 0; // Don't sort non-sortable columns
      }

      const rawA = a[orderBy];
      const rawB = b[orderBy];

      const aVal = rawA ?? (typeof rawB === 'number' ? 0 : '');
      const bVal = rawB ?? (typeof rawA === 'number' ? 0 : '');

      let comparison = 0;

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        comparison = (aVal ? 1 : 0) - (bVal ? 1 : 0);
      } else {
        comparison = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
      }

      return order === 'asc' ? comparison : -comparison;
    });
  }, [models, order, orderBy, filtersState]);

  const renderIcon = useCallback((model: ModelDefinition) => {
    if (!model.icon) {
      return null;
    }

    const iconDisplay: unknown = (modelIconMap as Record<string, unknown>)[model.icon];
    if (typeof iconDisplay === 'string') {
      return <img src={iconDisplay} width="20px" height="20px" />;
    }

    if (!React.isValidElement(iconDisplay)) {
      return null;
    }

    return React.cloneElement(iconDisplay as React.ReactElement<React.SVGProps<SVGSVGElement>>, {
      width: 20,
      height: 20,
    });
  }, []);

  const renderLinks = useCallback(
    (model: ModelDefinition) => {
      const termsUrl = model.termsUrls?.[0];
      const additionalTermsLink = model.termsUrls?.[1];
      const privacyUrl = model.privacyPolicyUrl;

      return (
        <FlexCenVer sx={{ gap: 0.5, flexWrap: 'wrap' }}>
          {termsUrl && (
            <Link href={termsUrl} target="_blank" rel="noopener noreferrer" color={color.White80_T}>
              {t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.TABLE.LINKS.TERMS)}
              {privacyUrl || additionalTermsLink ? ',' : ''}
            </Link>
          )}

          {privacyUrl && (
            <Link href={privacyUrl} target="_blank" rel="noopener noreferrer" color={color.White80_T}>
              {t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.TABLE.LINKS.PRIVACY)}
              {additionalTermsLink ? ',' : ''}
            </Link>
          )}
          {additionalTermsLink && (
            <Link href={additionalTermsLink} target="_blank" rel="noopener noreferrer" color={color.White80_T}>
              {t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.TABLE.LINKS.ADDITIONAL_TERMS)}
            </Link>
          )}
        </FlexCenVer>
      );
    },
    [t],
  );

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SET_SEARCH', payload: e.target.value });
  }, []);

  const handleSearchClear = useCallback(() => {
    dispatch({ type: 'SET_SEARCH', payload: '' });
  }, []);

  const handleAllowedFilterChange = useCallback((value: BooleanFilter) => {
    dispatch({ type: 'SET_ALLOWED_FILTER', payload: value });
  }, []);

  const handleStatusFilterChange = useCallback((value: BooleanFilter) => {
    dispatch({ type: 'SET_STATUS_FILTER', payload: value });
  }, []);

  const handleSortClick = useCallback(
    (headCellId: HeadCellId) => () => {
      handleRequestSort(headCellId);
    },
    [handleRequestSort],
  );

  const handleModelToggle = useCallback(
    (modelId: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      handleAllowedToggle(modelId, e.target.checked);
    },
    [handleAllowedToggle],
  );

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <>
      <Flex sx={{ flexWrap: 'wrap' }}>
        <Input
          placeholder={t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.TABLE.SEARCH_PLACEHOLDER)}
          value={filtersState.search}
          onChange={handleSearchChange}
          sx={{ width: 240, mr: 0.5 }}
          noBg
          startAdornment={<SearchIcon />}
          endAdornment={
            filtersState.search ? (
              <XIcon width={16} height={16} style={{ cursor: 'pointer' }} onClick={handleSearchClear} />
            ) : null
          }
        />
        <ModelFilters
          allowedFilter={filtersState.allowedFilter}
          statusFilter={filtersState.statusFilter}
          onAllowedFilterChange={handleAllowedFilterChange}
          onStatusFilterChange={handleStatusFilterChange}
          hideStatusFilter={shouldBlockUnverifiedModels}
        />
      </Flex>

      <TableContainer className="wea-no-scrollbar" sx={{ backgroundColor: 'none' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ height: 40 }}>
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  padding="none"
                  sortDirection={headCell.sortable && orderBy === headCell.id ? order : false}
                  sx={{ width: getColWidth(headCell.id), pl: 2 }}
                >
                  {headCell.sortable ? (
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={handleSortClick(headCell.id)}
                    >
                      <LabelSmRg
                        text={headCell.label}
                        sx={{ color: orderBy === headCell.id ? color.White100 : color.White64_T }}
                      />
                    </TableSortLabel>
                  ) : (
                    <Typography variant="label-sm-rg" sx={{ color: color.White64_T }}>
                      {headCell.label}
                    </Typography>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedModels.length === 0 ? (
              <StyledTableRow>
                <StyledTableCell colSpan={headCells.length} sx={{ textAlign: 'center', py: 4 }}>
                  <TextSmRg
                    text={t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.TABLE.NO_MODELS_FOUND)}
                    color={color.White64_T}
                  />
                </StyledTableCell>
              </StyledTableRow>
            ) : (
              filteredAndSortedModels.map((model) => (
                <StyledTableRow hover key={model.modelId}>
                  <StyledTableCell sx={{ pl: 2 }}>
                    <FlexCenVer sx={{ gap: 0.75 }}>
                      {renderIcon(model)}
                      <FlexCol>
                        <TextSmMd sx={{ display: 'flex', gap: 1 }}>
                          {model.displayName || model.name}
                          {model.isNew && (
                            <Box component="span" sx={{ position: 'relative', top: '-1px' }}>
                              <Tag
                                variant="large"
                                text={t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.NEW_INDICATOR)}
                                textColor={color.Yellow100}
                                bgColor={color.Yellow16_T}
                                sx={{ px: 0.5 }}
                              />
                            </Box>
                          )}
                        </TextSmMd>
                      </FlexCol>
                    </FlexCenVer>
                  </StyledTableCell>

                  <StyledTableCell sx={{ pl: 2 }}>
                    {model.isVerified && <VerifiedSeal textVariant="body-sm-rg" textColor={color.White80_T} />}
                  </StyledTableCell>

                  {!shouldBlockUnverifiedModels && (
                    <StyledTableCell sx={{ pl: 2 }}>{!model.isVerified && renderLinks(model)}</StyledTableCell>
                  )}
                  <StyledTableCell sx={{ pl: 2 }}>
                    <TextSmRg
                      text={model.createdAt ? format(new Date(model.createdAt), DATE_FORMATS.SLASHED_DATE) : 'N/A'}
                      color={color.White80_T}
                    />
                  </StyledTableCell>
                  <StyledTableCell sx={{ pl: 2 }}>
                    <FlexCenVer sx={{ gap: 1 }}>
                      <AppSwitch
                        checked={model.isAllowed}
                        disabled={updatingModelIds.has(model.modelId)}
                        onChange={handleModelToggle(model.modelId)}
                        inputProps={{ 'aria-label': `allow model ${model.displayName || model.name}` }}
                      />
                      {updatingModelIds.has(model.modelId) && (
                        <CircularProgress size={10} sx={{ color: color.White64_T }} />
                      )}
                    </FlexCenVer>
                  </StyledTableCell>
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
